import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { FISCAL_YEARS } from "@/constants/modules";
import { toast } from "@/lib/toast";
import { logger } from "@/lib/logger";
import { Calculator, Plus, Search, Trash2, Edit2, Save, X, Info, Download, Upload as UploadIcon, Copy, History, AlertCircle } from "lucide-react";
import TaxRatesSubModule from "./tax-lines/TaxRatesSubModule.jsx";
import TaxLinesBaseSubModule from "./tax-lines/TaxLinesBaseSubModule.jsx";
import LineHistoryModal from "./tax-lines/LineHistoryModal.jsx";
import DuplicateYearModal from "./tax-lines/DuplicateYearModal.jsx";

// Fallback groups if API fails; will be replaced by /tax-lines/sections server response.
const FALLBACK_GROUPS = [
  "Revenus", "Deductions", "Revenu imposable",
  "Credits non remboursables", "Impot", "Annexes et ajustements",
  "Credits remboursables", "Retenues et acomptes", "Resultats", "Autre",
];

const SORT_OPTIONS = [
  { id: "line_number_asc", label: "Ligne ↑" },
  { id: "line_number_desc", label: "Ligne ↓" },
  { id: "amount_desc", label: "Montant ↓" },
  { id: "amount_asc", label: "Montant ↑" },
  { id: "group_asc", label: "Section" },
  { id: "updated_desc", label: "Recent" },
];

export default function TaxLinesModule() {
  const defaultYear = FISCAL_YEARS[0] || new Date().getFullYear() - 1;
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get("view") === "base" ? "base"
                    : searchParams.get("view") === "rates" ? "rates"
                    : "lines";
  const [view, setView] = useState(initialView);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [year, setYear] = useState(defaultYear);
  const [jurisdiction, setJurisdiction] = useState("federal");
  const [sections, setSections] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [sort, setSort] = useState("line_number_asc");

  const [lines, setLines] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [commonLines, setCommonLines] = useState([]);
  const [commonMeta, setCommonMeta] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [newLine, setNewLine] = useState({ line_number: "", label: "", amount: 0, notes: "", group: "Autre" });
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [historyLine, setHistoryLine] = useState(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  useEffect(() => { loadClients(); loadSections(); }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reload client lines when any filter changes
  useEffect(() => {
    if (selectedClient) loadLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, year, jurisdiction, groupFilter, sort, debouncedSearch]);

  // Reload common reference lines when jurisdiction/year changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCommonLines(); }, [jurisdiction, year]);

  const loadClients = async () => {
    try { const r = await api.get("/clients"); setClients(r.data); } catch { /* silent */ }
  };

  const loadSections = async () => {
    try {
      const r = await api.get("/tax-lines/sections");
      setSections(r.data?.sections || []);
    } catch { setSections([]); }
  };

  const loadLines = async () => {
    setLoading(true);
    try {
      const params = { tax_year: year, jurisdiction, sort, limit: 500, skip: 0 };
      if (groupFilter) params.group = groupFilter;
      if (debouncedSearch) params.q = debouncedSearch;
      const r = await api.get(`/clients/${selectedClient}/tax-lines`, { params });
      // New shape : {items, total, has_more}
      setLines(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
    } catch { setLines([]); setTotal(0); setHasMore(false); }
    finally { setLoading(false); }
  };

  const loadCommonLines = async () => {
    try {
      const r = await api.get("/tax-lines/common-lines", { params: { jurisdiction, tax_year: year } });
      setCommonLines(r.data?.lines || []);
      setCommonMeta({ tax_year: r.data?.tax_year, source_year: r.data?.source_year });
    } catch { setCommonLines([]); setCommonMeta(null); }
  };

  // ── Validation ────────────────────────────────────────────────────────
  const validate = (ln) => {
    const errs = {};
    const num = String(ln.line_number || "").trim();
    if (!num) errs.line_number = "Numero requis";
    else if (!/^[A-Za-z0-9.\-]{1,10}$/.test(num)) errs.line_number = "Alphanum (max 10)";
    if (!String(ln.label || "").trim()) errs.label = "Description requise";
    if (String(ln.label || "").length > 300) errs.label = "Max 300 chars";
    if (typeof ln.amount === "string" && isNaN(parseFloat(ln.amount.replace(",", ".")))) errs.amount = "Montant invalide";
    return errs;
  };

  const handleAdd = async () => {
    const errs = validate(newLine);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Veuillez corriger les erreurs"); return; }
    try {
      await api.post("/tax-lines", {
        client_id: selectedClient,
        tax_year: year,
        jurisdiction,
        line_number: String(newLine.line_number).trim(),
        label: String(newLine.label).trim(),
        amount: parseFloat(newLine.amount) || 0,
        notes: newLine.notes || "",
        group: newLine.group || "Autre",
      });
      toast.success("Ligne ajoutee");
      setAdding(false);
      setCustomMode(false);
      setNewLine({ line_number: "", label: "", amount: 0, notes: "", group: "Autre" });
      setErrors({});
      loadLines();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur creation");
    }
  };

  const handleUpdate = async (id) => {
    const errs = validate(editData);
    if (Object.keys(errs).length) { toast.error("Veuillez corriger : " + Object.values(errs).join(", ")); return; }
    try {
      await api.put(`/tax-lines/${id}`, {
        line_number: String(editData.line_number).trim(),
        label: String(editData.label).trim(),
        amount: parseFloat(editData.amount) || 0,
        notes: editData.notes || "",
        group: editData.group || "Autre",
      });
      toast.success("Ligne mise a jour");
      setEditingId(null);
      loadLines();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette ligne ?")) return;
    try { await api.delete(`/tax-lines/${id}`); toast.success("Supprimee"); loadLines(); }
    catch { toast.error("Erreur"); }
  };

  const selectCommonLine = (cl) => {
    setNewLine({ ...newLine, line_number: cl.line_number, label: cl.label, group: cl.group || "Autre" });
    setAdding(true);
    setCustomMode(false);
    setErrors({});
  };

  const handleExport = async () => {
    try {
      const r = await api.get("/tax-lines/export", {
        params: { client_id: selectedClient, tax_year: year, jurisdiction },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tax_lines_${selectedClient || "all"}_${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV telecharge");
    } catch { toast.error("Erreur export"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/tax-lines/import", fd, {
        params: { client_id: selectedClient, tax_year: year, jurisdiction },
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported, skipped, errors: errs } = r.data;
      toast.success(`${imported} ligne(s) importee(s), ${skipped} ignoree(s)`);
      if (errs?.length) logger.warn("Import warnings:", errs);
      loadLines();
    } catch (er) { toast.error(er.response?.data?.detail || "Erreur import"); }
    finally { e.target.value = ""; }
  };

  // Group lines by section for display
  const groupedLines = useMemo(() => {
    const map = {};
    lines.forEach(ln => {
      const g = ln.group || "Autre";
      if (!map[g]) map[g] = [];
      map[g].push(ln);
    });
    return map;
  }, [lines]);

  // Commonly referenced lines grouped for sidebar
  const filteredCommon = useMemo(() => {
    if (!debouncedSearch) return commonLines;
    const q = debouncedSearch.toLowerCase();
    return commonLines.filter(cl => `${cl.line_number} ${cl.label}`.toLowerCase().includes(q));
  }, [commonLines, debouncedSearch]);

  const commonGrouped = useMemo(() => {
    const map = {};
    filteredCommon.forEach(cl => {
      const g = cl.group || "Autre";
      if (!map[g]) map[g] = [];
      map[g].push(cl);
    });
    return map;
  }, [filteredCommon]);

  const groupOptions = useMemo(
    () => (sections.length ? sections.map(s => s.id) : FALLBACK_GROUPS),
    [sections]
  );

  const totalAmount = useMemo(
    () => lines.reduce((acc, l) => acc + (Number(l.amount) || 0), 0),
    [lines]
  );

  if (view === "rates") return <TaxRatesSubModule year={year} setYear={setYear} onBack={() => setView("lines")} />;
  if (view === "base") return <TaxLinesBaseSubModule onBack={() => setView("lines")} />;

  return (
    <div className="max-w-6xl mx-auto" data-testid="tax-lines-module">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Lignes fiscales</h1>
          <p className="text-sm text-gray-500 mt-0.5">Saisie ligne par ligne — annee {year}</p>
        </div>
        {/* Batch #14.121 — Boutons "Base lignes fiscales" + "Taux d'imposition" retirés.
            Accès séparés via Fiscal Hub > Base fiscale et > Taux d'imposition. */}
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Client</label>
          <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tl-client-select">
            <option value="">Choisir...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}`}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Annee</label>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tl-year-select">
            {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Juridiction</label>
          <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tl-jurisdiction-select">
            <option value="federal">Federal</option>
            <option value="provincial">Provincial (Quebec)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Tri</label>
          <select value={sort} onChange={e => setSort(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tl-sort-select">
            {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {commonMeta && commonMeta.source_year && commonMeta.source_year !== year && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 mb-4 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Les lignes de reference pour {year} ne sont pas encore publiees — affichage des lignes {commonMeta.source_year}. Les numeros restent generalement stables d'une annee a l'autre.</span>
        </div>
      )}

      {selectedClient && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left : common lines library */}
          <div className="bg-white border rounded-xl overflow-hidden lg:col-span-1" data-testid="common-lines-panel">
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher une ligne..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                  data-testid="tl-search" />
              </div>
              <button onClick={() => { setCustomMode(true); setAdding(true); setNewLine({ line_number: "", label: "", amount: 0, notes: "", group: "Autre" }); setErrors({}); }}
                className="w-full bg-green-50 text-green-700 border border-green-200 px-3 py-2 rounded-lg text-xs font-medium hover:bg-green-100 flex items-center justify-center gap-1"
                data-testid="create-custom-line-btn">
                <Plus className="w-3 h-3" /> Creer une ligne personnalisee
              </button>
            </div>
            <div className="max-h-[500px] overflow-auto">
              {Object.entries(commonGrouped).map(([group, cls]) => (
                <div key={group}>
                  <div className="px-3 py-1.5 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide sticky top-0 z-10">{group}</div>
                  {cls.map(cl => (
                    <button key={`${cl.line_number}-${cl.label}`}
                      onClick={() => selectCommonLine(cl)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 transition text-sm border-b border-gray-50"
                      data-testid={`common-line-${cl.line_number}`}>
                      <span className="font-mono text-xs text-blue-600 mr-2">L.{cl.line_number}</span>
                      <span className="text-gray-700">{cl.label}</span>
                    </button>
                  ))}
                </div>
              ))}
              {!filteredCommon.length && (
                <div className="px-4 py-6 text-center text-gray-400 text-sm">Aucune ligne trouvee</div>
              )}
            </div>
          </div>

          {/* Right : client lines */}
          <div className="bg-white border rounded-xl overflow-hidden lg:col-span-2" data-testid="client-lines-panel">
            <div className="p-3 border-b flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium">{total} ligne(s)</span>
                <span className="text-xs text-gray-500">Total : <span className="font-semibold text-gray-800 tabular-nums">{totalAmount.toFixed(2)} $</span></span>
                <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-xs" data-testid="tl-group-filter">
                  <option value="">Toutes sections</option>
                  {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <button onClick={handleExport} className="border px-2 py-1 rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1 text-gray-700" title="Exporter CSV" data-testid="tl-export-btn">
                  <Download className="w-3 h-3" /> Export
                </button>
                <label className="border px-2 py-1 rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1 text-gray-700 cursor-pointer" title="Importer CSV" data-testid="tl-import-btn">
                  <UploadIcon className="w-3 h-3" /> Import
                  <input type="file" accept=".csv" onChange={handleImport} className="hidden" />
                </label>
                <button onClick={() => setDuplicateOpen(true)} className="border border-emerald-200 text-emerald-700 px-2 py-1 rounded-lg text-xs hover:bg-emerald-50 flex items-center gap-1" title="Dupliquer d'une autre annee" data-testid="tl-duplicate-btn">
                  <Copy className="w-3 h-3" /> Dupliquer
                </button>
                <button onClick={() => { setAdding(true); setCustomMode(false); setErrors({}); }}
                  className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"
                  data-testid="add-tax-line-btn">
                  <Plus className="w-3 h-3" /> Ajouter
                </button>
              </div>
            </div>

            {adding && (
              <div className="p-3 border-b bg-indigo-50 space-y-2" data-testid="tl-add-form">
                {customMode && <p className="text-xs font-medium text-green-700">Nouvelle ligne personnalisee</p>}
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="w-24">
                    <label className="block text-xs mb-1">Ligne</label>
                    <input type="text" value={newLine.line_number}
                      onChange={e => setNewLine({...newLine, line_number: e.target.value})}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm font-mono ${errors.line_number ? "border-red-300 bg-red-50" : ""}`}
                      placeholder="99999" data-testid="tl-line-number" />
                    {errors.line_number && <p className="text-[10px] text-red-500 mt-0.5">{errors.line_number}</p>}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs mb-1">Description</label>
                    <input type="text" value={newLine.label}
                      onChange={e => setNewLine({...newLine, label: e.target.value})}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm ${errors.label ? "border-red-300 bg-red-50" : ""}`}
                      placeholder="Nom de la ligne" data-testid="tl-label" />
                    {errors.label && <p className="text-[10px] text-red-500 mt-0.5">{errors.label}</p>}
                  </div>
                  <div className="w-28">
                    <label className="block text-xs mb-1">Montant ($)</label>
                    <input type="number" step="0.01" value={newLine.amount}
                      onChange={e => setNewLine({...newLine, amount: e.target.value})}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm text-right ${errors.amount ? "border-red-300 bg-red-50" : ""}`}
                      data-testid="tl-amount" />
                    {errors.amount && <p className="text-[10px] text-red-500 mt-0.5">{errors.amount}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="w-48">
                    <label className="block text-xs mb-1">Section</label>
                    <select value={newLine.group} onChange={e => setNewLine({...newLine, group: e.target.value})}
                      className="w-full border rounded-lg px-2 py-1.5 text-sm" data-testid="tl-group-select">
                      {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="block text-xs mb-1">Notes (optionnel)</label>
                    <input type="text" value={newLine.notes}
                      onChange={e => setNewLine({...newLine, notes: e.target.value})}
                      className="w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="Note..." />
                  </div>
                  <button onClick={handleAdd}
                    className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm disabled:opacity-50"
                    data-testid="tl-confirm-add">Ajouter</button>
                  <button onClick={() => { setAdding(false); setCustomMode(false); setNewLine({ line_number: "", label: "", amount: 0, notes: "", group: "Autre" }); setErrors({}); }}
                    className="border px-3 py-1.5 rounded-lg text-sm">Annuler</button>
                </div>
              </div>
            )}

            <div className="divide-y">
              {loading && (
                <div className="px-4 py-10 text-center text-gray-400">
                  <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
                </div>
              )}
              {!loading && Object.entries(groupedLines).map(([group, gLines]) => {
                const groupSum = gLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
                return (
                  <div key={group}>
                    <div className="px-4 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center justify-between">
                      <span>{group} <span className="font-normal text-gray-400">({gLines.length})</span></span>
                      <span className="tabular-nums text-gray-600">{groupSum.toFixed(2)} $</span>
                    </div>
                    {gLines.map(line => (
                      <div key={line.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 border-b border-gray-50" data-testid={`line-${line.id}`}>
                        {editingId === line.id ? (
                          <>
                            <input type="text" value={editData.line_number || ""}
                              onChange={e => setEditData({...editData, line_number: e.target.value})}
                              className="w-20 border rounded px-2 py-1 text-sm font-mono" />
                            <input type="text" value={editData.label || ""}
                              onChange={e => setEditData({...editData, label: e.target.value})}
                              className="flex-1 border rounded px-2 py-1 text-sm" />
                            <select value={editData.group || "Autre"}
                              onChange={e => setEditData({...editData, group: e.target.value})}
                              className="w-32 border rounded px-2 py-1 text-xs">
                              {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                            <input type="number" step="0.01" value={editData.amount ?? 0}
                              onChange={e => setEditData({...editData, amount: e.target.value})}
                              className="w-28 border rounded px-2 py-1 text-sm text-right" />
                            <button onClick={() => handleUpdate(line.id)} className="text-green-600 hover:text-green-800" data-testid="save-edit-btn"><Save className="w-4 h-4" /></button>
                            <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                          </>
                        ) : (
                          <>
                            <span className="font-mono text-xs text-blue-600 w-16 flex-shrink-0">L.{line.line_number}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate">{line.label}</p>
                              {line.notes && <p className="text-xs text-gray-400">{line.notes}</p>}
                            </div>
                            <span className="text-sm font-bold tabular-nums">{Number(line.amount || 0).toFixed(2)} $</span>
                            <button onClick={() => setHistoryLine(line)} className="text-gray-300 hover:text-purple-500" title="Historique" data-testid={`history-line-${line.id}`}><History className="w-4 h-4" /></button>
                            <button onClick={() => { setEditingId(line.id); setEditData({ ...line }); }} className="text-gray-300 hover:text-blue-500" data-testid={`edit-line-${line.id}`}><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(line.id)} className="text-gray-300 hover:text-red-500" data-testid={`delete-line-${line.id}`}><Trash2 className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              {!loading && !lines.length && (
                <div className="px-4 py-10 text-center text-gray-400" data-testid="tl-empty">
                  <Calculator className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Aucune ligne saisie</p>
                  <p className="text-xs mt-1">Selectionnez une ligne dans la liste ou creez-en une personnalisee</p>
                </div>
              )}
              {hasMore && (
                <div className="px-4 py-3 text-center text-xs text-amber-600 bg-amber-50">
                  <Info className="w-3 h-3 inline mr-1" />
                  Affichage limite a 500 lignes. Utilisez la recherche/filtres pour affiner.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {historyLine && (
        <LineHistoryModal line={historyLine} onClose={() => setHistoryLine(null)} />
      )}
      {duplicateOpen && (
        <DuplicateYearModal
          clientId={selectedClient}
          currentYear={year}
          jurisdiction={jurisdiction}
          onClose={() => setDuplicateOpen(false)}
          onDone={() => { setDuplicateOpen(false); loadLines(); }}
        />
      )}
    </div>
  );
}
